/**
 * Draft figures for the Umsatzsteuervoranmeldung (UVA) and the Einnahmen-Ausgaben-Rechnung.
 *
 * The Kennzahlen follow form U30. This produces a draft to check and copy into FinanzOnline, not
 * a filing: only booked documents are counted, and the report says how many are still unbooked.
 *
 * Which date decides the period depends on the basis: an Einnahmen-Ausgaben-Rechner taxes when
 * the money moves (Ist-Besteuerung, so the payment date), double-entry bookkeeping when the
 * invoice is issued (Soll-Besteuerung).
 */
import { afaSchedule, type AssetInput } from "./afa";
import { isWithin, type Period } from "./periods";
import { travelLog, type TripInput } from "./travel";
import { parseVatLines, round2, type TaxTreatment, type VatLine } from "./vat";

export const UVA_CODES = [
  "000", // Gesamtbetrag der Bemessungsgrundlage
  "011", // davon steuerfrei: Ausfuhrlieferungen (§ 6 Abs 1 Z 1 iVm § 7)
  "017", // davon steuerfrei: innergemeinschaftliche Lieferungen (Art 6 Abs 1)
  "016", // davon steuerfrei: Kleinunternehmer (§ 6 Abs 1 Z 27)
  "020", // davon steuerfrei: übrige Umsätze ohne Vorsteuerabzug (§ 6 Abs 1 Z ...)
  "021", // Umsätze mit Übergang der Steuerschuld auf den Leistungsempfänger
  "022", // Bemessungsgrundlage 20 % Normalsteuersatz
  "029", // Bemessungsgrundlage 10 % ermäßigter Steuersatz
  "006", // Bemessungsgrundlage 13 % ermäßigter Steuersatz
  "037", // Bemessungsgrundlage 19 % (Jungholz und Mittelberg)
  "124", // Bemessungsgrundlage 4,9 % Grundnahrungsmittel (seit 1. Juli 2026)
  "057", // Steuerschuld aus Reverse Charge (Leistungen aus dem Ausland)
  "060", // Gesamtbetrag der abziehbaren Vorsteuer
  "061", // Vorsteuer aus entrichteter Einfuhrumsatzsteuer (§ 12 Abs 1 Z 2 lit a)
  "065", // Vorsteuer aus innergemeinschaftlichem Erwerb
  "066", // Vorsteuer aus der Steuerschuld nach Reverse Charge
  "070", // Bemessungsgrundlage innergemeinschaftlicher Erwerbe
  "072", // davon 20 %
  "073", // davon 10 %
  "008", // davon 13 %
  "088", // davon 19 % (Jungholz und Mittelberg)
  "125", // davon 4,9 % (seit 1. Juli 2026)
  "095", // Zahllast (+) oder Gutschrift (-)
] as const;
export type UvaCode = (typeof UVA_CODES)[number];

/**
 * Bemessungsgrundlage Kennzahl per VAT rate, for turnover we invoice ourselves. 124 and 125 (the
 * 4,9 % since 1 July 2026) are named in the BMF's FAQ on the rate cut; the U30 does not split input
 * tax by rate, so 4,9 % Vorsteuer goes to 060 like every other.
 */
export const REVENUE_BASE_CODE: Record<number, UvaCode> = {
  20: "022",
  10: "029",
  13: "006",
  19: "037",
  4.9: "124",
};
/** The same for intra-community acquisitions. */
export const ACQUISITION_BASE_CODE: Record<number, UvaCode> = {
  20: "072",
  10: "073",
  13: "008",
  19: "088",
  4.9: "125",
};

export type UvaDocument = {
  kind: "ER" | "AR" | "KA";
  status: "inbox" | "review" | "booked" | "archived";
  taxTreatment?: TaxTreatment;
  issueDate?: string;
  paidDate?: string;
  netTotal?: number;
  vatTotal?: number;
  vatLines?: unknown;
};

export type AccountingBasis = "cash" | "accrual";

export type UvaResult = {
  amounts: Record<UvaCode, number>;
  /** VAT we owe, input tax we may deduct, and the difference (Kennzahl 095). */
  vatOwed: number;
  inputTax: number;
  payable: number;
  bookedCount: number;
  /** Documents in the period that are not booked yet, so the draft is still incomplete. */
  pendingCount: number;
};

/** The date that decides which period a document belongs to. */
export function relevantDate(document: UvaDocument, basis: AccountingBasis): string | undefined {
  if (basis === "cash") return document.paidDate || document.issueDate;
  return document.issueDate;
}

function emptyAmounts(): Record<UvaCode, number> {
  return Object.fromEntries(UVA_CODES.map((code) => [code, 0])) as Record<UvaCode, number>;
}

/** VAT a document carries, computed from the rates when the supplier charged none (RC, i.g.). */
function selfAssessedVat(lines: VatLine[], vatTotal: number | undefined): number {
  if (vatTotal && vatTotal > 0) return round2(vatTotal);
  return round2(lines.reduce((sum, line) => sum + (line.net * line.rate) / 100, 0));
}

export function computeUva(
  documents: UvaDocument[],
  period: Period,
  basis: AccountingBasis,
): UvaResult {
  const amounts = emptyAmounts();
  let vatOwed = 0;
  let inputTax = 0;
  let bookedCount = 0;
  let pendingCount = 0;

  const add = (code: UvaCode, value: number) => {
    amounts[code] = round2(amounts[code] + value);
  };

  for (const document of documents) {
    if (document.status === "archived") continue;
    if (!isWithin(relevantDate(document, basis), period)) continue;
    if (document.status !== "booked") {
      pendingCount += 1;
      continue;
    }
    bookedCount += 1;

    const lines = parseVatLines(document.vatLines);
    const net = round2(document.netTotal ?? lines.reduce((sum, line) => sum + line.net, 0));
    const treatment: TaxTreatment = document.taxTreatment ?? "standard";

    if (document.kind === "AR") {
      add("000", net);
      if (treatment === "standard") {
        for (const line of lines) {
          const code = REVENUE_BASE_CODE[line.rate];
          if (code) add(code, line.net);
          vatOwed = round2(vatOwed + line.vat);
        }
      } else if (treatment === "eu_intra_community") {
        add("017", net);
      } else if (treatment === "export") {
        add("011", net);
      } else if (treatment === "reverse_charge") {
        add("021", net);
      } else if (treatment === "small_business") {
        add("016", net);
      } else if (treatment === "no_vat") {
        add("020", net);
      }
      continue;
    }

    // Incoming: ER and KA.
    if (treatment === "eu_intra_community") {
      // Innergemeinschaftlicher Erwerb: we owe the Austrian VAT and deduct it in the same breath.
      const vat = selfAssessedVat(lines, document.vatTotal);
      add("070", net);
      for (const line of lines) {
        const code = ACQUISITION_BASE_CODE[line.rate];
        if (code) add(code, line.net);
      }
      vatOwed = round2(vatOwed + vat);
      add("065", vat);
      inputTax = round2(inputTax + vat);
    } else if (treatment === "reverse_charge") {
      const vat = selfAssessedVat(lines, document.vatTotal);
      add("057", vat);
      vatOwed = round2(vatOwed + vat);
      add("066", vat);
      inputTax = round2(inputTax + vat);
    } else if (treatment === "standard" || treatment === "export") {
      // Austrian input tax (060), or import VAT already paid at the border (061): both deductible.
      const vat = round2(document.vatTotal ?? 0);
      add(treatment === "export" ? "061" : "060", vat);
      inputTax = round2(inputTax + vat);
    }
  }

  const payable = round2(vatOwed - inputTax);
  add("095", payable);
  return { amounts, vatOwed, inputTax, payable, bookedCount, pendingCount };
}

export type StatementCategory = {
  id: string;
  name: string;
  accountNumber?: string;
  deductiblePercent?: number;
};

export type StatementDocument = UvaDocument & {
  id?: string;
  category?: string;
  grossTotal?: number;
  deductiblePercent?: number;
  /** Trinkgeld: no VAT, so it is an expense at its full amount, deductible like the receipt. */
  tip?: number;
};

export type StatementRow = {
  categoryId: string;
  name: string;
  accountNumber?: string;
  amount: number;
  /** After the partial deduction, e.g. half of a Bewirtungsspesen entry. */
  deductible: number;
  count: number;
};

export type IncomeStatement = {
  revenue: StatementRow[];
  expenses: StatementRow[];
  revenueTotal: number;
  expenseTotal: number;
  /** Revenue minus the deductible share of the expenses. */
  profit: number;
};

/**
 * Income or expense that is not a document: a statement row booked to a category without a
 * receipt, or a year's AfA. Euros, positive. `deductible` defaults to the category's share.
 */
export type StatementLine = {
  side: "revenue" | "expense";
  /** The category's id, or a fixed key such as "afa". */
  key: string;
  name: string;
  accountNumber?: string;
  date: string;
  amount: number;
  deductible?: number;
};

/**
 * Rows booked to a category without a receipt. Money out is an expense of an expense category (a
 * refund reduces it), money in revenue of a revenue category. Rows before their account's opening
 * day belong to the books before.
 */
export function receiptlessLines(
  rows: Array<{
    amount: number;
    bookingDate: string;
    status: string;
    bookingType?: string;
    category?: string;
    account: string;
  }>,
  categories: Array<StatementCategory & { kind: "revenue" | "expense" }>,
  openingDates: Record<string, string | undefined> = {},
): StatementLine[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const lines: StatementLine[] = [];
  for (const row of rows) {
    if (row.status !== "ignored" || row.bookingType !== "category" || !row.category) continue;
    const category = byId.get(row.category);
    const date = row.bookingDate.slice(0, 10);
    if (!category || date < (openingDates[row.account] ?? "").slice(0, 10)) continue;
    lines.push({
      side: category.kind,
      key: category.id,
      name: category.name,
      accountNumber: category.accountNumber,
      date,
      amount: round2(category.kind === "revenue" ? row.amount : -row.amount),
    });
  }
  return lines;
}

/**
 * Every asset's AfA as expense lines, dated like the journal: 31 December, or the day it was given
 * up. The private share is never deductible.
 */
export function depreciationLines(
  assets: AssetInput[],
  label: { name: string; accountNumber?: string },
): StatementLine[] {
  return assets.flatMap((asset) =>
    afaSchedule(asset).map((row) => ({
      side: "expense" as const,
      key: "afa",
      name: label.name,
      accountNumber: label.accountNumber,
      date:
        asset.disposalDate && asset.disposalDate.startsWith(String(row.year))
          ? asset.disposalDate.slice(0, 10)
          : `${row.year}-12-31`,
      amount: round2((row.depreciation + row.residual) / 100),
      deductible: round2(row.deductible / 100),
    })),
  );
}

/**
 * The Fahrtenbuch as expense lines: each trip's Kilometergeld, Tagesgeld and Nächtigungsgeld on
 * the day it started, after the year's kilometre cap (`travelLog`).
 */
export function travelLines(
  trips: Array<TripInput & { id: string }>,
  label: { name: string; accountNumber?: string },
): StatementLine[] {
  return travelLog(trips)
    .filter((trip) => trip.amounts.total > 0)
    .map((trip) => ({
      side: "expense" as const,
      key: "travel",
      name: label.name,
      accountNumber: label.accountNumber,
      date: trip.date.slice(0, 10),
      amount: trip.amounts.total / 100,
    }));
}

/**
 * Einnahmen-Ausgaben-Rechnung by category.
 * VAT-registered businesses report net amounts (the VAT is a transitory item); a Kleinunternehmer
 * cannot reclaim input tax, so gross amounts are their cost - pass `useGross` for that case.
 */
export function computeIncomeStatement(
  documents: StatementDocument[],
  categories: StatementCategory[],
  period: Period,
  basis: AccountingBasis,
  options: {
    useGross?: boolean;
    uncategorizedLabel?: string;
    /** Receipt-less bookings and AfA (`receiptlessLines`, `depreciationLines`). */
    lines?: StatementLine[];
    /** Purchases in the asset register: their AfA is the expense, not the invoice. */
    excludeDocuments?: ReadonlySet<string>;
  } = {},
): IncomeStatement {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const revenue = new Map<string, StatementRow>();
  const expenses = new Map<string, StatementRow>();
  const uncategorized = options.uncategorizedLabel ?? "—";

  for (const document of documents) {
    if (document.status !== "booked") continue;
    if (document.id && options.excludeDocuments?.has(document.id)) continue;
    if (!isWithin(relevantDate(document, basis), period)) continue;

    const amount = round2(
      ((options.useGross ? document.grossTotal : document.netTotal) ??
        document.netTotal ??
        document.grossTotal ??
        0) + (document.kind === "AR" ? 0 : (document.tip ?? 0)),
    );
    if (amount === 0) continue;

    const category = document.category ? byId.get(document.category) : undefined;
    const key = category?.id ?? "uncategorized";
    const target = document.kind === "AR" ? revenue : expenses;
    const share =
      document.kind === "AR"
        ? 100
        : (document.deductiblePercent ?? category?.deductiblePercent ?? 100);

    const row = target.get(key) ?? {
      categoryId: key,
      name: category?.name ?? uncategorized,
      accountNumber: category?.accountNumber,
      amount: 0,
      deductible: 0,
      count: 0,
    };
    row.amount = round2(row.amount + amount);
    row.deductible = round2(row.deductible + (amount * share) / 100);
    row.count += 1;
    target.set(key, row);
  }

  for (const line of options.lines ?? []) {
    if (line.amount === 0 || !isWithin(line.date, period)) continue;
    const category = byId.get(line.key);
    const target = line.side === "revenue" ? revenue : expenses;
    const share = line.side === "revenue" ? 100 : (category?.deductiblePercent ?? 100);
    const row = target.get(line.key) ?? {
      categoryId: line.key,
      name: category?.name ?? line.name,
      accountNumber: category?.accountNumber ?? line.accountNumber,
      amount: 0,
      deductible: 0,
      count: 0,
    };
    row.amount = round2(row.amount + line.amount);
    row.deductible = round2(row.deductible + (line.deductible ?? (line.amount * share) / 100));
    row.count += 1;
    target.set(line.key, row);
  }

  const sort = (rows: StatementRow[]) =>
    rows.sort((a, b) => (a.accountNumber ?? "zzz").localeCompare(b.accountNumber ?? "zzz"));

  const revenueRows = sort([...revenue.values()]);
  const expenseRows = sort([...expenses.values()]);
  const revenueTotal = round2(revenueRows.reduce((sum, row) => sum + row.amount, 0));
  const expenseTotal = round2(expenseRows.reduce((sum, row) => sum + row.deductible, 0));

  return {
    revenue: revenueRows,
    expenses: expenseRows,
    revenueTotal,
    expenseTotal,
    profit: round2(revenueTotal - expenseTotal),
  };
}

/**
 * How much of the Kleinunternehmer turnover limit is used up (§ 6 Abs 1 Z 27 UStG, 55.000 € net
 * since 2025). Returns a share of the limit so the UI can warn before it is crossed.
 */
export function smallBusinessUsage(
  documents: UvaDocument[],
  year: number,
  limit: number,
  basis: AccountingBasis,
): { turnover: number; limit: number; share: number } {
  const turnover = round2(
    documents
      .filter(
        (document) =>
          document.kind === "AR" &&
          document.status === "booked" &&
          (relevantDate(document, basis) ?? "").startsWith(String(year)),
      )
      .reduce((sum, document) => sum + (document.netTotal ?? 0), 0),
  );
  return { turnover, limit, share: limit > 0 ? round2(turnover / limit) : 0 };
}
